import React, { useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Button, Row, Col, Card, OverlayTrigger, Tooltip, Modal, Form, InputGroup } from 'react-bootstrap';

// External libraries
import * as Yup from 'yup';
import CsLineIcons from 'cs-line-icons/CsLineIcons';
import { handlerErrors } from 'utils/handleErrors';

// components
// import ModalParcel from './ModalParcel';
import { ToastSuccess } from '../../../../components/alerts/toast';
import ButtonRequest from '../../../../components/buttons/ButtonRequest';

// api
import { editParcel, deleteParcel, getAllParcels } from '../../../../api/Api-user';

// reducer
import { setParcels } from '../../../../slices/generalData/generalData';
import { useHandleResponse } from '../../../../hooks/useHandleResponse';

// External libraries
const { FormatMoney } = require('format-money-js');

const fmDecimal = new FormatMoney({
  decimals: 0,
});

const CardParcel = ({ parcel, setTrigger }) => {
  const dispatch = useDispatch();
  const buttonRef = useRef(null);
  const { handleResponse } = useHandleResponse();

  const [isModalDeleteOpen, setIsModalDeleteOpen] = useState(false);
  // - Local State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isBtnDisabled, setIsBtnDisabled] = useState(false);
  const [parcelErr, setParcelErr] = useState({
    name: '',
    product_description: '',
    height: '',
    width: '',
    depth: '',
    weight: '',
  });
  // - Local State
  const [parcelEdit, setParcel] = useState({
    name: parcel.name,
    product_description: parcel.product_description,
    height: parcel.height,
    width: parcel.width,
    depth: parcel.depth,
    weight: parcel.weight,
    product_value: parcel.product_value ? parcel.product_value : 0,
    insurance: parcel.insurance,
  });

  // - Data Validation and Updating States with Reducers
  const updateField = (data, validation, field) => {
    if (data === '' || validation.test(data)) setParcel({ ...parcelEdit, [field]: data });
  };
  const handleCloseEdition = () => {
    setIsModalOpen(false);
    // Esperar un poco para que el modal cierre y luego quitar el foco
    setTimeout(() => {
      if (buttonRef.current) {
        buttonRef?.current.blur();
      }
    }, 500);
  };

  // Schema Validation
  const validationParcelSchema = Yup.object().shape({
    name: Yup.string().min(3, 'El nombre debe tener al menos 3 caracteres.').required('Por favor, ingresa el nombre.'),
    product_description: Yup.string()
      .min(3, 'La descripción del producto debe tener al menos 3 caracteres.')
      .required('Por favor, ingresa la descripción del producto.'),
    height: Yup.string().required('Por favor, ingresa la altura del paquete.'),
    width: Yup.string().required('Por favor, ingresa el ancho del paquete.'),
    depth: Yup.string().required('Por favor, ingresa la profundidad del paquete.'),
    weight: Yup.string().required('Por favor, ingresa el peso del paquete.'),
  });
  const updateParcels = async () => {
    try {
      localStorage.removeItem('parcels');
      const response = await getAllParcels();

      if (response?.parcel) {
        // 1. Guardar la nueva data en localStorage
        localStorage.setItem('parcels', JSON.stringify(response.parcel));

        // 2. Actualizar el estado global
        dispatch(setParcels({ data: response.parcel }));
      }
    } catch (error) {
      console.error('Error al obtener paquetes:', error);
    }
  };
  const handlerDeleteParcel = async () => {
    try {
      setIsBtnDisabled(true);
      const response = await deleteParcel(parcel.id);
      // ('response delete:', response);
      if (response.status === 206) {
        setIsModalDeleteOpen(false);
      }
      handleResponse(response, (res) => {
        if (res?.data.is_deleted !== undefined) {
          setIsModalOpen(false);
          ToastSuccess('✨¡Paquete eliminado correctamente!');
          setTrigger((prev) => !prev);
          setIsModalDeleteOpen(false);
          updateParcels();
        }
      });
    } catch (error) {
      handlerErrors(error);
    } finally {
      setIsBtnDisabled(false);
    }
  };
  const handlerEditParcel = async () => {
    try {
      setIsBtnDisabled(true);
      const response = await editParcel(parcel.id, parcelEdit);
      // console.log('response ok:', response);
      if (response.status === 206) {
        handleCloseEdition();
      }
      handleResponse(response, (res) => {
        if (res?.data.name !== undefined) {
          setIsModalOpen(false);
          ToastSuccess('✨¡Paquete editado correctamente!');
          handleCloseEdition();
          setTrigger((prev) => !prev);
          updateParcels();
        }
      });
      // if (response.name !== undefined) {
      //   setIsModalOpen(false);
      //   ToastSuccess('✨¡Paquete editado correctamente!');
      //   handleCloseEdition();
      //   setTrigger((prev) => !prev);
      //   updateParcels();
      // }
    } catch (error) {
      handlerErrors(error);
    } finally {
      setIsBtnDisabled(false);
    }
  };

  const validateForm = async (e) => {
    e.preventDefault();
    try {
      await validationParcelSchema.validate(parcel, { abortEarly: false });
      handlerEditParcel();
    } catch (errors) {
      const formErrors = {};
      errors.inner.forEach((error) => {
        setParcelErr({ ...parcelErr, [error.path]: error.message });
        formErrors[error.path] = error.message;
      });
      // console.log('errores en from:', formErrors);
      // formAddressFromRef.current.scrollIntoView({ behavior: 'smooth' }); // scroll to error
    }
  };

  return (
    <>
      {/* Edit Address Modal Start */}
      <Modal show={isModalOpen} size="lg" onHide={handleCloseEdition}>
        <Modal.Header closeButton>
          <Modal.Title className="text-primary">Editar paquete</Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-3">
          <Form onSubmit={validateForm} className="ps-3 me-4">
            <Row className="g-3">
              <Col sm="12">
                <Form.Label>Nombre</Form.Label>
                <span className="text-primary">*</span>
                <div style={{ position: 'relative', zIndex: 1 }}>
                  <InputGroup className="mb-1">
                    <InputGroup.Text>
                      <CsLineIcons icon="box" size="17" className="text-alternate" />
                    </InputGroup.Text>
                    <Form.Control
                      maxLength={35}
                      placeholder="máximo 35 caracteres"
                      onChange={(e) => {
                        setParcelErr({ ...parcelErr, name: '' });
                        updateField(e.target.value, /^[A-Z0-9 ]+$/i, 'name');
                      }}
                      value={parcelEdit.name}
                      type="text"
                    />
                    {parcelErr.name && <div className={`invalid-tooltip ${parcelErr.name ? 'd-block' : 'd-none'}`}>{parcelErr.name}</div>}
                  </InputGroup>
                </div>
              </Col>
              <Col sm="12">
                <Form.Label>Descripción</Form.Label>
                <span className="text-primary">*</span>
                <div style={{ position: 'relative', zIndex: 1 }}>
                  <InputGroup className="mb-1">
                    <InputGroup.Text>
                      <CsLineIcons icon="edit-square" size="17" className="text-alternate" />
                    </InputGroup.Text>
                    <Form.Control
                      maxLength={35}
                      placeholder="máximo 35 caracteres"
                      onChange={(e) => {
                        setParcelErr({ ...parcelErr, product_description: '' });
                        updateField(e.target.value, /^[A-Z0-9 ]+$/i, 'product_description');
                      }}
                      value={parcelEdit.product_description}
                      type="text"
                    />
                    {parcelErr.product_description && (
                      <div className={`invalid-tooltip ${parcelErr.product_description ? 'd-block' : 'd-none'}`}>{parcelErr.product_description}</div>
                    )}
                  </InputGroup>
                </div>
              </Col>
              <Col xs="12" lg="6">
                <Form.Label>Dimensiones: largo, ancho y alto (en cm)</Form.Label>
                <span className="text-primary">*</span>
                <InputGroup className="mb-1">
                  <InputGroup.Text>
                    <CsLineIcons icon="box" size="17" className="text-alternate" />
                  </InputGroup.Text>
                  <Form.Control
                    placeholder="Largo"
                    maxLength={3}
                    onChange={(e) => {
                      setParcelErr({ ...parcelErr, depth: '' });
                      updateField(e.target.value, /^[0-9]*$/, 'depth');
                    }}
                    value={parcelEdit.depth}
                  />

                  <InputGroup.Text>
                    <CsLineIcons icon="multiply" size="17" className="text-alternate" />
                  </InputGroup.Text>
                  <Form.Control
                    placeholder="Ancho"
                    maxLength={3}
                    onChange={(e) => {
                      setParcelErr({ ...parcelErr, width: '' });
                      updateField(e.target.value, /^[0-9]*$/, 'width');
                    }}
                    value={parcelEdit.width}
                  />
                  <InputGroup.Text>
                    <CsLineIcons icon="multiply" size="17" className="text-alternate" />
                  </InputGroup.Text>
                  <Form.Control
                    placeholder="Alto"
                    maxLength={3}
                    onChange={(e) => {
                      setParcelErr({ ...parcelErr, height: '' });
                      updateField(e.target.value, /^[0-9]*$/, 'height');
                    }}
                    value={parcelEdit.height}
                  />
                </InputGroup>
              </Col>
              <Col sm="6">
                <Form.Label>Peso</Form.Label>
                <span className="text-primary">*</span>
                <div style={{ position: 'relative', zIndex: 1 }}>
                  <InputGroup className="mb-1">
                    <InputGroup.Text>
                      <CsLineIcons icon="download" size="17" className="text-alternate" />
                    </InputGroup.Text>
                    <Form.Control
                      maxLength={2}
                      onChange={(e) => {
                        setParcelErr({ ...parcelErr, weight: '' });
                        updateField(e.target.value, /^[0-9]*$/, 'weight');
                      }}
                      value={parcelEdit.weight}
                      type="text"
                    />
                    {parcelErr.weight && <div className={`invalid-tooltip ${parcelErr.weight ? 'd-block' : 'd-none'}`}>{parcelErr.weight}</div>}
                  </InputGroup>
                </div>
              </Col>
              <Col sm="6">
                <Form.Label>Valor del contenido</Form.Label>
                <OverlayTrigger
                  delay={{ show: 500, hide: 0 }}
                  placement="top"
                  overlay={
                    // .tooltip this is its class
                    <Tooltip id="tooltip-top">
                      Este valor es obligatorio para la carta porte. No genera cargos extra, excepto si decides asegurar el envío, en cuyo caso se usará para
                      calcular el seguro.
                    </Tooltip>
                  }
                >
                  <span className="cursor-pointer">
                    <CsLineIcons icon="info-hexagon" size="20" className="text-alternate mb-1" />
                  </span>
                </OverlayTrigger>
                <div style={{ position: 'relative', zIndex: 1 }}>
                  <InputGroup className="mb-1">
                    <InputGroup.Text>
                      <CsLineIcons icon="dollar" size="17" className="text-alternate" />
                    </InputGroup.Text>
                    <Form.Control
                      maxLength={5}
                      onChange={(e) => {
                        updateField(e.target.value, /^[0-9]*$/, 'product_value');
                      }}
                      value={parcelEdit.product_value}
                      type="text"
                    />
                  </InputGroup>
                </div>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex justify-content-end ">
            <Button variant="outline-primary" className="me-2" onClick={handleCloseEdition}>
              Cancelar
            </Button>
            <ButtonRequest title="Editar" titleLoading="Editando" handlerFn={handlerEditParcel} isButtonDisabled={isBtnDisabled} isLoading={isBtnDisabled} />
            {/* <Button variant="primary" onClick={handlerEditParcel}>
              Editar
            </Button> */}
          </div>
        </Modal.Footer>
      </Modal>
      {/* Edit Address Modal End */}
      {/* Delete Address Modal Star */}
      <Modal show={isModalDeleteOpen} onHide={() => setIsModalDeleteOpen(false)}>
        <Modal.Header closeButton>
          <Modal.Title className="text-primary" />
        </Modal.Header>
        <Modal.Body className="bg-infow">
          <div className="text-semi-large mb-4 text-primary">{parcel?.name.toUpperCase()}</div>
          <div className="text-semi-large mb-3">¿Estas segur@ de que deseas elimimar esta paquete?</div>
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex justify-content-end ">
            <Button variant="outline-primary" className="me-2" onClick={() => setIsModalDeleteOpen(false)}>
              Cancelar
            </Button>
            <ButtonRequest
              title="Si, Eliminar"
              titleLoading="Eliminando"
              handlerFn={handlerDeleteParcel}
              isButtonDisabled={isBtnDisabled}
              isLoading={isBtnDisabled}
            />
          </div>
        </Modal.Footer>
      </Modal>
      {/* Delete Address Modal End */}
      <Card className="mb-2">
        <Card.Body className="py-3 py-lg-0 sh-auto sh-sm-auto sh-lg-8">
          <Row className="g-0 h-100 align-content-center">
            <Col
              xs="12"
              sm="9"
              lg="3"
              className="mb-2 pe-lg-4 mb-lg-0 d-flex flex-column justify-content-center order-1 order-lg-1 h-lg-100 position-relative bg-infox"
            >
              <div className="text-muted text-small d-lg-none">Nombre</div>
              <div className="text-primary text-truncate">{parcel.name.toUpperCase()}</div>
            </Col>
            <Col xs="12" sm="12" lg="3" className="mb-2 mb-lg-0 pe-lg-4 d-flex flex-column justify-content-center order-3 order-lg-2 mb-3 mb-lg-0">
              <div className="text-muted text-small d-lg-none">Descripción</div>
              <div className="text-alternate">{parcel.product_description.toUpperCase()}</div>
            </Col>
            <Col xs="6" lg="1" className="d-flex flex-column justify-content-center align-items-lg-start mb-2 mb-lg-0 order-4 order-lg-3">
              <div className="text-muted text-small d-lg-none">Largo</div>
              <div className="text-alternate">
                {parcel.depth} <span className="text-small text-muted">cm</span>
              </div>
            </Col>
            <Col xs="6" lg="1" className="d-flex flex-column justify-content-center mb-2 mb-lg-0 order-5 order-lg-4">
              <div className="text-muted text-small d-lg-none">Ancho</div>
              <div className="text-alternate">
                {parcel.width} <span className="text-small text-muted">cm</span>
              </div>
            </Col>
            <Col xs="6" lg="1" className="d-flex flex-column justify-content-center  mb-2 mb-lg-0 order-5 order-lg-4">
              <div className="text-muted text-small d-lg-none">Alto</div>
              <div className="text-alternate">
                {parcel.height} <span className="text-small text-muted">cm</span>
              </div>
            </Col>
            <Col xs="6" lg="1" className="d-flex flex-column justify-content-center align-items-lg-start mb-2 mb-lg-0 order-last order-lg-5">
              <div className="text-muted text-small d-lg-none mb-1">Kg</div>
              <div className="text-alternate">{parcel.weight}</div>
            </Col>
            <Col xs="12" lg="1" className="d-flex flex-column justify-content-center align-items-end mb-2 mb-lg-0 order-last order-lg-5 ">
              <div className="text-muted text-small d-lg-none mb-1">Valor del contenido</div>
              <span className="text-alternate">
                <span className="text-small text-muted">$ </span>
                {parcel.product_value === '' ? 0 : fmDecimal.from(parseInt(parcel.product_value, 10))}
              </span>
            </Col>
            <Col
              xs="12"
              sm="3"
              lg="1"
              className="d-flex justify-content-end align-items-center mb-2 mt-2 mt-lg-0 mb-lg-0 order-2 order-lg-last order-lg-5 bg-warningx"
            >
              <OverlayTrigger delay={{ show: 300, hide: 0 }} placement="top" overlay={<Tooltip id="tooltip-top">Editar</Tooltip>}>
                <div className="d-flex flex-column align-items-center justify-content-center me-3">
                  <div className="text-muted text-small d-lg-none">Editar</div>
                  <div ref={buttonRef} onClick={() => setIsModalOpen(true)} className="cursor-pointer">
                    <CsLineIcons icon="edit" size="18" className="btn-link" />
                  </div>
                </div>
              </OverlayTrigger>
              <OverlayTrigger delay={{ show: 300, hide: 0 }} placement="top" overlay={<Tooltip id="tooltip-top">Eliminar</Tooltip>}>
                <div className="d-flex flex-column align-items-end justify-content-center">
                  <div className="text-muted text-small d-lg-none">Eliminar</div>
                  <span className="cursor-pointer" onClick={() => setIsModalDeleteOpen(true)}>
                    <CsLineIcons icon="bin" size="18" className="btn-link" />
                  </span>
                </div>
              </OverlayTrigger>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </>
  );
};
export default CardParcel;
